import { SelectChangeEvent } from '@mui/material';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchAdminDashboardData,
    fetchDashboardConnectivityState,
    fetchDashboardGpuState,
    IAdminDashboardData,
    IConnectivityData,
} from '../../service/adminDashboardService';
import { fetchDeviceList, IDeviceData, IUserData } from '../../service/deviceListService';
import { RootState, store } from '../../store';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { updateDeviceDataState, updateDeviceListState } from '../../store/reducers/deviceSlice';
import { IGpuState } from './type';

const useAdminDashboard = () => {
    const dispatch = useDispatch();
    const [initialLoad, setInitialLoad] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { deviceList, deviceData } = useSelector((state: RootState) => state?.devices);
    const [value, setValue] = useState(0);
    const [selectedDevice, setSelectedDevice] = useState('');
    const [userList, setUserList] = useState<Array<IUserData>>([]);
    const [gpuStateData, setGpuStateData] = useState<Array<IGpuState>>([]);
    const [connectivityStatusData, setConnectivityStateData] = useState<Array<IConnectivityData>>([]);
    const [dashboardData, setDashBoardData] = useState<IAdminDashboardData>();
    const [openUserListPopup, setOpenListPopup] = useState(false);

    const deviceListHandler = React.useCallback(
        (deviceArray: string[]) => {
            if (!Array.isArray(deviceArray) || deviceArray?.length === 0) return;
            const { deviceList, deviceData } = store.getState().devices;
            const tempDeviceList = [...deviceList];
            let tempDeviceData = { ...deviceData };
            const deviceIds = Object.keys(tempDeviceData);
            const notAvailableDeviceIds = deviceArray.filter(x => !deviceIds.includes(x));
            notAvailableDeviceIds.forEach((deviceId: string) => {
                const deviceIdTemp = deviceId?.trim();
                if (deviceIdTemp === '') return;
                tempDeviceList.push({
                    id: tempDeviceList?.length,
                    device_id: deviceIdTemp,
                });
                tempDeviceData = {
                    ...tempDeviceData,
                    [deviceIdTemp]: [],
                };
            });
            dispatch(updateDeviceListState(tempDeviceList));
            dispatch(updateDeviceDataState(tempDeviceData));
        },
        [dispatch],
    );

    const fetchInitialData = React.useCallback(async () => {
        try {
            dispatch(showLoader());
            const dashBoardData = await fetchAdminDashboardData();
            deviceListHandler(dashBoardData?.device_list || []);
            if (dashBoardData) {
                setDashBoardData(dashBoardData);
            }
            setInitialLoad(true);
            dispatch(hideLoader());
        } catch (err) {
            dispatch(hideLoader());
        }
    }, [dispatch, deviceListHandler]);

    const fetchDeviceData = React.useCallback(async () => {
        try {
            dispatch(showLoader());
            const deviceObj: IDeviceData = await fetchDeviceList();
            const { deviceList, deviceData } = deviceObj;
            if (Array.isArray(deviceList) && deviceList?.length > 0) {
                dispatch(updateDeviceListState(deviceList));
                dispatch(updateDeviceDataState(deviceData));
            }
        } catch (err) {
            dispatch(hideLoader());
        } finally {
            dispatch(hideLoader());
            fetchInitialData();
        }
    }, [dispatch, fetchInitialData]);

    const selectDeviceUserData = React.useCallback(
        (deviceId: string): IUserData[] => {
            if (!deviceData) return [];
            const userDataList = deviceData?.[deviceId];
            return Array.isArray(userDataList) ? userDataList : [];
        },
        [deviceData],
    );

    useEffect(() => {
        if (Array.isArray(deviceList) && deviceList?.length > 0) {
            setSelectedDevice(deviceList?.[0]?.device_id);
            setUserList(selectDeviceUserData(deviceList?.[0]?.device_id));
        }
    }, [deviceList, selectDeviceUserData]);
    useEffect(() => {
        fetchDeviceData();
    }, [fetchDeviceData]);

    const handleDeviceChange = (event: SelectChangeEvent) => {
        setSelectedDevice(event.target.value as string);
        setUserList(selectDeviceUserData(event.target.value));
    };
    const onUserViewMoreHandler = () => {
        setOpenListPopup(true);
    };
    const onUserPopUpCloseHandler = () => {
        setOpenListPopup(false);
        setAnchorEl(null);
    };

    const fetchDeviceRelatedData = React.useCallback(async () => {
        try {
            if (value === 0) {
                setGpuStateData([]);
                dispatch(showLoader());
                const gpuStateData = await fetchDashboardGpuState(selectedDevice);
                setGpuStateData(gpuStateData);
                dispatch(hideLoader());
            } else {
                setConnectivityStateData([]);
                dispatch(showLoader());
                const connectivityStateData = await fetchDashboardConnectivityState(selectedDevice);
                setConnectivityStateData(connectivityStateData);
                dispatch(hideLoader());
            }
        } catch (err) {
            dispatch(hideLoader());
        }
    }, [value, selectedDevice, dispatch]);
    useEffect(() => {
        if (!initialLoad) return;
        fetchDeviceRelatedData();
    }, [selectedDevice, value, initialLoad, fetchDeviceRelatedData]);

    const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    return {
        deviceList,
        selectedDevice,
        handleDeviceChange,
        dashboardData,
        value,
        setValue,
        onUserViewMoreHandler,
        openUserListPopup,
        onUserPopUpCloseHandler,
        userList,
        gpuStateData,
        connectivityStatusData,
        handleAvatarClick,
        anchorEl,
    };
};
export { useAdminDashboard };
